"use client"

import { ampli } from "@/ampli"
import { ConnectionsPreview } from "@/components/ConnectionsPreview"
import { Avatar, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { MarkerPinIcon } from "@/icons/solid"
import { BreadCrumbRef, NovaSession } from "@/types"
import { createUrlWithBreadCrumb } from "@/utils/breadCrumbs"
import clsx from "clsx"
import Link from "next/link"
import React, { useState, type JSX } from "react"
import { ImageSection } from "./ImageSection"
import { MessageAndInviteDialogButtons } from "./MessageAndInviteDialogButtons"

const GridBusinessCardFragment = graphql(`
  fragment GridBusinessCard on IndividualCreative {
    __typename
    id
    displayName
    professionString
    avatarInitials
    avatar168: avatar(width: 168)
    handle
    locationName

    user {
      id
      name
      ...ConnectionsPreview
    }

    ...CreativeBusinessCardImagesSection
    ...MessageAndInviteDialogButtons
  }
`)

interface Props {
  creative: FragmentType<typeof GridBusinessCardFragment> | undefined | null
  actionButtons?: JSX.Element
  onlyShowActionsOnHover?: boolean
  serverSession: NovaSession
  breadCrumbRef?: BreadCrumbRef
  trackingEntryPoint: string
}

export const GridBusinessCard: React.FC<Props> = props => {
  const creative = useFragment(GridBusinessCardFragment, props.creative)
  const { actionButtons, onlyShowActionsOnHover = true, serverSession, breadCrumbRef, trackingEntryPoint } = props

  const [inviteToAJobDialogOpen, setInviteToAJobDialogOpen] = useState(false)

  const profileUrl = `/${creative?.handle}`
  const url = createUrlWithBreadCrumb({
    url: profileUrl,
    breadCrumbRef,
  })

  return (
    <div className={clsx("relative h-[368px] w-full", onlyShowActionsOnHover && "md:h-[306px]")}>
      <div className="absolute left-0 top-0 h-full w-full">
        <div className="group relative flex w-full flex-col overflow-hidden rounded-xl border border-bg-3 bg-bg-1 py-6 transition-all duration-300 md:hover:z-20 md:hover:shadow-[0_8px_35px_rgba(0,0,0,0.2)] md:group-hover:z-20 md:group-hover:shadow-[0_8px_35px_rgba(0,0,0,0.2)]">
          <Link
            href={url}
            className=""
            onClick={() => {
              ampli.businessCardViewProfile({
                entry_point: trackingEntryPoint,
              })
            }}>
            <div className="mb-5 flex flex-row items-center justify-between px-6">
              <div className="flex flex-row items-center">
                <Avatar initials={creative?.avatarInitials} src={creative?.avatar168} size="xl" />
                <div className="ml-4 flex flex-col">
                  <Text variant="body1" maxLines={2}>
                    {creative?.displayName || creative?.user.name}
                  </Text>
                  <Text variant="label" className="text-fg-2" maxLines={2}>
                    {creative?.professionString}
                  </Text>
                </div>
              </div>
              <div className="flex flex-col items-end pl-2">
                <div className="flex flex-row items-center justify-end gap-x-1">
                  <MarkerPinIcon height={12} className="-mr-[2px] fill-fg-4 stroke-fg-4" />
                  <Text variant="body3" className="text-fg-2">
                    {creative?.locationName}
                  </Text>
                </div>
                <div className="mt-2 flex flex-shrink-0">
                  <ConnectionsPreview user={creative?.user} textVariant="body2" serverSession={serverSession} />
                </div>
              </div>
            </div>
            <div className="relative z-10">
              <ImageSection
                creative={creative}
                type="grid"
                showTextOverlay={false}
                serverSession={serverSession}
                entryPoint={trackingEntryPoint}
              />
            </div>
          </Link>

          <div
            className={clsx(
              "relative mt-5 flex flex-row items-end gap-x-2 px-6",
              onlyShowActionsOnHover &&
                "z-5 transition-all duration-200 ease-in-out group-hover:mt-5 group-hover:opacity-100 md:-mt-10 md:opacity-0"
            )}>
            {actionButtons || (
              <MessageAndInviteDialogButtons
                creative={creative}
                trackingEntryPoint={trackingEntryPoint}
                dialogOpen={inviteToAJobDialogOpen}
                onCloseDialog={() => setInviteToAJobDialogOpen(false)}
                onOpenDialog={() => setInviteToAJobDialogOpen(true)}
                serverSession={serverSession}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
